import React from "react"
import { useTranslation } from "react-i18next"
import { API_ROUTES } from "../../../../configuration/RoutesApi"
import { ButtonIcon } from "../../../../components/Button/Button"
import { PopupHelpButton } from "../../../../components/Help/PopupHelpButton"
import {
  faArrowsRotate,
  faCircleNodes,
  faDownload,
  faPlugCircleCheck,
  faPlus,
  faPrint,
  faSignal,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { MetricCard } from "../../../../components/MetricCard/MetricCard"
import Animate, { AnimationTypes } from "../../../../components/Animate/Animate"

type PairingData = {
  agentId: number
  pairingCode: string
  expiresAt: string
}

type Props = {
  totalAgents: number
  onlineAgents: number
  totalPrinters: number
  activePrinters: number
  pairingData: PairingData | null
  onOpenAddAgentModal: () => void
  onRefresh: () => void
}

export const PrintAutomationHeaderCard: React.FC<Props> = ({
  totalAgents,
  onlineAgents,
  totalPrinters,
  activePrinters,
  pairingData,
  onOpenAddAgentModal,
  onRefresh,
}) => {
  const { t } = useTranslation(["pages"])

  return (
    <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
      <div className="p3">
        <div className="flex mb-3 mt-3 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-bold text-slate-800 sm:text-xl">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-orange-100 text-orange-700">
                <FontAwesomeIcon icon={faPrint} className="text-sm" />
              </span>

              {t("label_print_automation_title")}
            </h2>
            <p className="mt-1 text-sm text-slate-500">{t("label_print_automation_agents_subtitle")}</p>
          </div>

          <PopupHelpButton
            helpKey="pages.print-automation"
            title={t("label_print_automation_title")}
            description={t("label_print_automation_agents_subtitle")}
            bullets={[
              t("label_print_automation_tip_download"),
              t("label_print_automation_tip_pair"),
              t("label_print_automation_tip_refresh"),
            ]}
          />
        </div>

        <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            title={t("label_print_automation_agents")}
            value={String(totalAgents)}
            icon={faCircleNodes}
            color="#475569"
          />

          <MetricCard
            title={t("label_print_automation_online")}
            value={String(onlineAgents)}
            icon={faSignal}
            color="#047857"
          />

          <MetricCard
            title={t("label_print_automation_printers")}
            value={String(totalPrinters)}
            icon={faPrint}
            color="#c2410c"
          />

          <MetricCard
            title={t("label_print_automation_active_printers_on_agent")}
            value={String(activePrinters)}
            icon={faPlugCircleCheck}
            color="#6d28d9"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <a
            href={`${API_ROUTES.URL_API}/downloads/print-agent/catalojei_print.rar`}
            className="inline-flex items-center gap-2 rounded-xl border border-orange-300 bg-gradient-to-r from-orange-500 to-red-500 px-3 py-2 text-sm font-bold text-white shadow-md transition hover:brightness-105"
            title={t("label_print_automation_download_agent")}
            data-interaction-description-key="interactionDescriptions:pages.myRestaurant.printAutomation.components.printAutomationHeaderCard.link.printAutomationDownloadAgent"
          >
            <ButtonIcon
              icon={faDownload}
              sizeNumber={34}
              bgColor="#c2410c"
              iconColor="white"
              descriptionKey="interactionDescriptions:pages.myRestaurant.printAutomation.components.printAutomationHeaderCard.button.faDownload"
            />
            <span>{t("label_print_automation_download_agent")}</span>
          </a>

          <button
            onClick={onOpenAddAgentModal}
            className="inline-flex items-center gap-2 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-700 shadow-sm transition hover:bg-emerald-100"
            data-interaction-description-key="interactionDescriptions:pages.myRestaurant.printAutomation.components.printAutomationHeaderCard.button.onOpenAddAgentModal"
          >
            <FontAwesomeIcon icon={faPlus} />
            {t("label_print_automation_add_agent")}
          </button>

          <button
            onClick={onRefresh}
            className="inline-flex items-center gap-2 rounded-xl border border-sky-300 bg-sky-50 px-4 py-2 text-sm font-bold text-sky-700 shadow-sm transition hover:bg-sky-100"
            data-interaction-description-key="interactionDescriptions:pages.myRestaurant.printAutomation.components.printAutomationHeaderCard.button.onRefresh"
          >
            <FontAwesomeIcon icon={faArrowsRotate} />
            {t("label_print_automation_refresh")}
          </button>
        </div>

        {pairingData && (
          <div className="mt-4 rounded-lg border-2 border-orange-200 bg-white p-3 text-sm text-orange-800">
            <p>
              <strong className="text-black">{t("label_print_automation_agent_id_label")}: </strong>

              <strong>{pairingData.agentId}</strong>
            </p>

            <p>
              <strong className="text-black">{t("label_print_automation_pairing_label")}: </strong>
              <strong>{pairingData.pairingCode}</strong>
            </p>

            <p>
              <strong className="text-black">{t("label_print_automation_expiration_label")}: </strong>
              <strong>{pairingData.expiresAt?.toReadableDate()}</strong>
            </p>
          </div>
        )}
      </div>
    </Animate>
  )
}
